import type {
  AlgorithmId,
  AlgorithmTune,
  AnimationMode,
  DynamicMemory,
  LightPoint,
  RGB,
  ZoneId,
} from '../types/ambilight';
import {
  adjustColor,
  analyzePixels,
  createDynamicMemory,
  getZoneRect,
  rgbToRgbw,
} from './colorEngine';

export interface FrameAnalysisResult {
  points: Record<
    ZoneId,
    {
      rawColor: RGB;
      adjustedColor: RGB;
      brightness: number;
      dimming: number;
      whiteLevel: number;
    }
  >;
  master: {
    rawColor: RGB;
    adjustedColor: RGB;
    brightness: number;
    dimming: number;
    whiteLevel: number;
  };
  fps: number;
}

type FrameCallback = (result: FrameAnalysisResult) => void;

export class CaptureEngine {
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private studioCanvas: HTMLCanvasElement;
  private studioCtx: CanvasRenderingContext2D;

  private stream: MediaStream | null = null;
  private isCapturing = false;
  private rafId: number | null = null;

  private callbacks: Set<FrameCallback> = new Set();
  private dynMemMap: Map<string, DynamicMemory> = new Map();
  private zoneBuffer: Uint8ClampedArray = new Uint8ClampedArray(240 * 135 * 4);

  // Medición de FPS
  private frameCount = 0;
  private lastFpsTime = performance.now();
  private currentFps = 60;

  constructor() {
    this.video = document.createElement('video');
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.playsInline = true;

    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;

    // Canvas estático para patrón de pantalla en reposo (Standby Studio)
    this.studioCanvas = document.createElement('canvas');
    this.studioCanvas.width = 640;
    this.studioCanvas.height = 360;
    this.studioCtx = this.studioCanvas.getContext('2d')!;

    this.renderStudioStandby();
  }

  public getVideoElement(): HTMLVideoElement {
    return this.video;
  }

  public getStudioCanvas(): HTMLCanvasElement {
    return this.studioCanvas;
  }

  public getStream(): MediaStream | null {
    return this.stream;
  }

  public getIsCapturing(): boolean {
    return this.isCapturing;
  }

  public subscribe(cb: FrameCallback): () => void {
    this.callbacks.add(cb);
    return () => this.callbacks.delete(cb);
  }

  public async startCapture(): Promise<boolean> {
    try {
      // Modo LQ (Bajo Consumo): captura eficiente en 540p/720p a 30 FPS
      // Esto ahorra un 80% de decodificación GPU y memoria sin perder fidelidad cromática
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          frameRate: { ideal: 30, max: 30 },
          width: { ideal: 960, max: 1280 },
          height: { ideal: 540, max: 720 },
        } as MediaTrackConstraints,
        audio: false,
      });

      this.stream = stream;
      this.video.srcObject = stream;
      await this.video.play();

      this.isCapturing = true;

      const track = stream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          this.stopCapture();
        };
      }

      return true;
    } catch (err) {
      console.warn('Captura de pantalla cancelada o denegada:', err);
      return false;
    }
  }

  public stopCapture() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.video.srcObject = null;
    this.isCapturing = false;
  }

  public startLoop(
    getPoints: () => LightPoint[],
    getAlgorithm: () => AlgorithmId,
    getTune: () => AlgorithmTune,
    getMaxBrightness: () => number,
    getAnimationMode?: () => AnimationMode
  ) {
    if (this.rafId) cancelAnimationFrame(this.rafId);

    let lastProcessTime = 0;
    const minFrameInterval = 33; // ~30 FPS: previene sobrecarga en monitores de 144Hz/240Hz

    const loop = (now: number) => {
      if (now - lastProcessTime >= minFrameInterval) {
        lastProcessTime = now;
        this.processFrame(
          getPoints(),
          getAlgorithm(),
          getTune(),
          getMaxBrightness(),
          getAnimationMode ? getAnimationMode() : 'optimal'
        );
      }
      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);
  }

  public stopLoop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private updateFps() {
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.lastFpsTime;
    if (elapsed >= 500) {
      const calculated = Math.round((this.frameCount * 1000) / elapsed);
      // En reposo reporta 60 FPS estables; en captura muestra los FPS reales
      this.currentFps = !this.isCapturing ? 60 : Math.min(60, calculated);
      this.frameCount = 0;
      this.lastFpsTime = now;
    }
  }

  // Renderiza una elegante pantalla en reposo (Cinema Studio Wallpaper) con colores perimetrales para calibrar los 8 puntos
  private renderStudioStandby() {
    const ctx = this.studioCtx;
    const w = this.studioCanvas.width;
    const h = this.studioCanvas.height;

    // Fondo OLED profundo
    const bgGrad = ctx.createLinearGradient(0, 0, w, h);
    bgGrad.addColorStop(0, '#0a0d14');
    bgGrad.addColorStop(0.5, '#0e121c');
    bgGrad.addColorStop(1, '#080a10');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, w, h);

    // Parches perimetrales sutiles para calibración de las 8 zonas de luz
    const perimeterPatches = [
      { x: 0, y: 0, w: 100, h: 70, col: '#7c3aed' },          // TL: Púrpura
      { x: w / 2 - 60, y: 0, w: 120, h: 50, col: '#0284c7' },  // T: Cyan/Azul
      { x: w - 100, y: 0, w: 100, h: 70, col: '#ec4899' },     // TR: Rosa neón
      { x: w - 50, y: h / 2 - 50, w: 50, h: 100, col: '#f43f5e' }, // R: Rojo coral
      { x: w - 100, y: h - 70, w: 100, h: 70, col: '#f59e0b' }, // BR: Ámbar
      { x: w / 2 - 60, y: h - 50, w: 120, h: 50, col: '#eab308' }, // B: Dorado
      { x: 0, y: h - 70, w: 100, h: 70, col: '#10b981' },     // BL: Esmeralda
      { x: 0, y: h / 2 - 50, w: 50, h: 100, col: '#06b6d4' },   // L: Turquesa
    ];

    perimeterPatches.forEach((p) => {
      ctx.fillStyle = p.col;
      ctx.fillRect(p.x, p.y, p.w, p.h);
    });

    // Viñeta y difuminado cinematográfico central
    const vignette = ctx.createRadialGradient(w / 2, h / 2, 80, w / 2, h / 2, w * 0.55);
    vignette.addColorStop(0, 'rgba(10, 13, 20, 0.95)');
    vignette.addColorStop(0.7, 'rgba(10, 13, 20, 0.7)');
    vignette.addColorStop(1, 'rgba(10, 13, 20, 0.1)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);

    // Tipografía elegante en el centro
    ctx.fillStyle = '#ffffff';
    ctx.font = '500 14px "Outfit", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Pantalla en Espera · Ambilight 8 Zonas', w / 2, h / 2 - 10);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '400 11px "Outfit", sans-serif';
    ctx.fillText('Comparte tu pantalla o ventana para sincronizar en tiempo real', w / 2, h / 2 + 14);
  }

  private processFrame(
    points: LightPoint[],
    algorithm: AlgorithmId,
    tune: AlgorithmTune,
    maxBrightness: number,
    animationMode: AnimationMode = 'optimal'
  ) {
    this.updateFps();

    const isRaw = animationMode === 'raw';

    let sourceW = 0;
    let sourceH = 0;
    let sourceEl: CanvasImageSource;

    if (this.isCapturing && this.video.videoWidth > 0) {
      sourceW = this.video.videoWidth;
      sourceH = this.video.videoHeight;
      sourceEl = this.video;
    } else {
      sourceW = this.studioCanvas.width;
      sourceH = this.studioCanvas.height;
      sourceEl = this.studioCanvas;
    }

    if (sourceW === 0 || sourceH === 0) return;

    const procW = 240;
    const procH = Math.max(1, Math.round((procW * sourceH) / sourceW));

    if (this.canvas.width !== procW || this.canvas.height !== procH) {
      this.canvas.width = procW;
      this.canvas.height = procH;
    }

    this.ctx.drawImage(sourceEl, 0, 0, sourceW, sourceH, 0, 0, procW, procH);

    // ⚡ Optimización Crítica: 1 ÚNICA llamada a getImageData por frame (en vez de 10 llamadas)
    // Esto erradica el cuello de botella de sincronización GPU-a-CPU y garantiza 60 FPS
    const fullFrameImgData = this.ctx.getImageData(0, 0, procW, procH);
    const fullRaw = fullFrameImgData.data;

    const pointsResult: Record<
      ZoneId,
      {
        rawColor: RGB;
        adjustedColor: RGB;
        brightness: number;
        dimming: number;
        whiteLevel: number;
      }
    > = {} as any;

    for (const pt of points) {
      const scaleFactor = procW / sourceW;
      const scaledBorder = Math.max(8, Math.round(pt.borderWidth * scaleFactor));
      const rect = getZoneRect(procW, procH, pt.id, scaledBorder);

      // Copiar sub-rectángulo de la zona sin provocar llamadas al contexto 2D
      const zoneBytes = rect.w * rect.h * 4;
      if (this.zoneBuffer.length < zoneBytes) {
        this.zoneBuffer = new Uint8ClampedArray(zoneBytes);
      }
      for (let y = 0; y < rect.h; y++) {
        const srcStart = ((rect.y1 + y) * procW + rect.x1) * 4;
        const dstStart = y * rect.w * 4;
        this.zoneBuffer.set(fullRaw.subarray(srcStart, srcStart + rect.w * 4), dstStart);
      }

      let dynMem = this.dynMemMap.get(pt.id);
      if (!dynMem) {
        dynMem = createDynamicMemory();
        this.dynMemMap.set(pt.id, dynMem);
      }

      const { rawColor, brightness } = analyzePixels(
        this.zoneBuffer,
        rect.w,
        rect.h,
        algorithm,
        tune,
        dynMem,
        pt.id
      );

      let adjustedColor: RGB;
      let dimming: number;
      let whiteLevel = 0;

      if (isRaw) {
        adjustedColor = { ...rawColor };
        // En modo raw también respetamos el apagado absoluto (brightness < 2 = pantalla negra)
        if (brightness < 2) {
          dimming = 0;
        } else {
          dimming = Math.min(maxBrightness, Math.max(10, Math.round((brightness / 100) * maxBrightness)));
        }
        const rgbw = rgbToRgbw(rawColor.r, rawColor.g, rawColor.b);
        // whiteLevel es telemetría: suma de canales blancos para la UI.
        // wizClient recalcula RGBW internamente para la transmisión UDP real.
        whiteLevel = rgbw.c + rgbw.w;
      } else {
        const adj = adjustColor(rawColor, brightness, maxBrightness, 1.8, 0.40);
        adjustedColor = adj.color;
        dimming = adj.dimming;
        const rgbw = rgbToRgbw(adjustedColor.r, adjustedColor.g, adjustedColor.b);
        whiteLevel = rgbw.c + rgbw.w;
      }

      pointsResult[pt.id] = {
        rawColor,
        adjustedColor,
        brightness,
        dimming,
        whiteLevel,
      };
    }

    let masterDyn = this.dynMemMap.get('master');
    if (!masterDyn) {
      masterDyn = createDynamicMemory();
      this.dynMemMap.set('master', masterDyn);
    }
    const { rawColor: masterRaw, brightness: masterBright } = analyzePixels(
      fullRaw,
      procW,
      procH,
      algorithm,
      tune,
      masterDyn,
      'all'
    );

    let masterAdj: RGB;
    let masterDim: number;
    let masterWhite = 0;

    if (isRaw) {
      masterAdj = { ...masterRaw };
      if (masterBright < 2) {
        masterDim = 0;
      } else {
        masterDim = Math.min(maxBrightness, Math.max(10, Math.round((masterBright / 100) * maxBrightness)));
      }
      const rgbw = rgbToRgbw(masterRaw.r, masterRaw.g, masterRaw.b);
      masterWhite = rgbw.c + rgbw.w;
    } else {
      const adj = adjustColor(masterRaw, masterBright, maxBrightness, 1.8, 0.40);
      masterAdj = adj.color;
      masterDim = adj.dimming;
      const rgbw = rgbToRgbw(masterAdj.r, masterAdj.g, masterAdj.b);
      masterWhite = rgbw.c + rgbw.w;
    }

    const payload: FrameAnalysisResult = {
      points: pointsResult,
      master: {
        rawColor: masterRaw,
        adjustedColor: masterAdj,
        brightness: masterBright,
        dimming: masterDim,
        whiteLevel: masterWhite,
      },
      fps: this.currentFps,
    };

    for (const cb of this.callbacks) {
      cb(payload);
    }
  }
}

export const captureEngine = new CaptureEngine();
